import { createTheme, darken, lighten, Theme } from '@mui/material';
import { DynamicBrandConfig } from 'src/context/DynamicBrandContext';
import { tokens } from 'switch-fe-shared/design';

export function createDynamicTheme(
  baseTheme: Theme,
  config: DynamicBrandConfig
): Theme {
  if (!config.primaryColor && !config.secondaryColor) {
    return baseTheme;
  }

  const primary = config.primaryColor ?? baseTheme.palette.primary.main;
  const secondary = config.secondaryColor ?? baseTheme.palette.secondary.main;
  const white = '#FFFFFF';
  const blackLighter40 = '#00000099';

  return createTheme(baseTheme, {
    palette: {
      primary: {
        main: primary,
        light: lighten(primary, 0.2),
        dark: darken(primary, 0.2),
      },
      secondary: {
        main: secondary,
        light: lighten(secondary, 0.2),
        dark: darken(secondary, 0.2),
      },
      action: {
        active: primary,
        hover: lighten(primary, 0.85),
        selected: darken(primary, 0.2),
        focus: primary,
      },
    },
    colors: {
      ...baseTheme.colors,
      primary: {
        lighter: lighten(primary, 0.85),
        light: lighten(primary, 0.3),
        main: primary,
        dark: darken(primary, 0.2),
      },
    },
    sidebar: {
      ...baseTheme.sidebar,
      menuItemColorActive: primary,
      menuItemIconColorActive: primary,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            backgroundColor: primary,
            border: `2px solid ${primary}`,
            ':hover': {
              backgroundColor: white,
              border: `2px solid ${primary}`,
            },
            ':active': {
              backgroundColor: blackLighter40,
              border: `2px solid ${blackLighter40}`,
            },
          },
          outlinedPrimary: {
            color: primary,
            border: `2px solid ${primary}`,
            ':hover': {
              backgroundColor: primary,
              border: `2px solid ${primary}`,
            },
          },
          outlinedSecondary: {
            ':active': {
              backgroundColor: primary,
              border: `2px solid ${primary}`,
            },
          },
          containedSecondary: {
            ':active': {
              backgroundColor: primary,
              border: `2px solid ${primary}`,
            },
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            '&.Mui-active': {
              color: primary,
            },
            '&.Mui-completed': {
              color: primary,
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: lighten(primary, 0.9),
            },
            '&.Mui-selected': {
              backgroundColor: primary,
              '&:hover': {
                backgroundColor: primary,
              },
            },
            '&.Mui-focusVisible': {
              backgroundColor: primary,
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          option: {
            '&:hover': {
              backgroundColor: lighten(primary, 0.9),
            },
            '&.Mui-focused': {
              backgroundColor: lighten(primary, 0.9),
            },
            '&[aria-selected="true"]': {
              backgroundColor: primary,
              '&:hover, &.Mui-focused': {
                backgroundColor: primary,
              },
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: lighten(primary, 0.95),
            },
            '&.Mui-selected': {
              backgroundColor: lighten(primary, 0.9),
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: lighten(primary, 0.85),
              '&:hover': {
                backgroundColor: lighten(primary, 0.3),
              },
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            color: primary,
            backgroundColor: lighten(primary, 0.85),
            '&:hover': {
              backgroundColor: lighten(primary, 0.85),
            },
            '&.Mui-selected': {
              backgroundColor: primary,
              color: tokens.colors.accentForeground,
              '&:hover': {
                backgroundColor: primary,
              },
            },
          },
        },
      },
    },
  });
}
